import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";
import { formatDateTime } from "../utils/dateUtils";

export default function AdminEnquiryDetailPage() {
  const { id } = useParams();
  const { http } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [enquiry, setEnquiry] = useState(null);
  const [workTypes, setWorkTypes] = useState([]);
  const [works, setWorks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [followups, setFollowups] = useState([]);

  const [form, setForm] = useState({
    workTypeId: "",
    workId: "",
    workDescription: "",
    paymentAmount: "",
    nextAppointmentAt: "",
    assignedToUserId: "",
    status: "",
  });

  const filteredWorks = useMemo(() => {
    if (!form.workTypeId) return [];
    return works.filter((w) => String(w.workTypeId) === String(form.workTypeId));
  }, [works, form.workTypeId]);

  const canSave = useMemo(() => {
    return (
      form.status ||
      form.workTypeId ||
      form.workId ||
      form.workDescription.trim() ||
      form.paymentAmount ||
      form.nextAppointmentAt ||
      form.assignedToUserId
    );
  }, [form]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [enqRes, wtRes, wRes, tmRes, fuRes] = await Promise.all([
          http.get(`/api/enquiries/${id}`),
          http.get("/api/master/work-types"),
          http.get("/api/master/works"),
          http.get("/api/admin/team-members"),
          http.get(`/api/enquiries/${id}/followups`),
        ]);
        if (!alive) return;
        setEnquiry(enqRes.data);
        setWorkTypes(wtRes.data.items);
        setWorks(wRes.data.items);
        setTeamMembers(tmRes.data.items);
        setFollowups(fuRes.data.items);
        setForm((s) => ({
          ...s,
          status: enqRes.data.status,
          assignedToUserId: enqRes.data.assignedToUserId || "",
        }));
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.message || "Failed to load enquiry.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [http, id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => {
      if (name === "workTypeId") {
        return { ...s, workTypeId: value, workId: "" };
      }
      if (name === "workId") {
        const selectedW = works.find((w) => String(w.id) === String(value));
        return {
          ...s,
          workId: value,
          paymentAmount: selectedW ? String(selectedW.fee) : s.paymentAmount,
          workDescription: selectedW ? selectedW.name : s.workDescription,
        };
      }
      return { ...s, [name]: value };
    });
  };

  const onSave = async () => {
    setError(null);
    try {
      await http.put(`/api/enquiries/${id}`, {
        status: form.status || undefined,
        assignedToUserId: form.assignedToUserId || null,
        workTypeId: form.workTypeId ? Number(form.workTypeId) : null,
        workId: form.workId ? Number(form.workId) : null,
        workDescription: form.workDescription || null,
        paymentAmount: form.paymentAmount ? Number(form.paymentAmount) : null,
        nextAppointmentAt: form.nextAppointmentAt || null,
      });
      const [enqRes, fuRes] = await Promise.all([
        http.get(`/api/enquiries/${id}`),
        http.get(`/api/enquiries/${id}/followups`),
      ]);
      setEnquiry(enqRes.data);
      setFollowups(fuRes.data.items);
      setForm((s) => ({
        ...s,
        workTypeId: "",
        workId: "",
        workDescription: "",
        paymentAmount: "",
        nextAppointmentAt: "",
      }));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save changes.");
    }
  };

  const downloadDocument = (docId, docName) => {
    const token = localStorage.getItem("rto_crm_token");
    fetch(`${http.defaults.baseURL}/api/documents/${docId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to download.");
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = docName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err.message || "Download failed."));
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  if (loading) return <div className="text-muted">Loading...</div>;
  if (!enquiry) return <div className="text-muted">Not found.</div>;

  return (
    <div className="d-grid gap-3">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <h4 className="mb-0">Enquiry</h4>
          <div className="text-muted small">
            Application No: <span className="fw-semibold">{enquiry.applicationNo}</span>
          </div>
        </div>
        <Link className="btn btn-outline-secondary" to="/admin">
          Back
        </Link>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="row g-3">
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">Customer Details</h6>
              <div className="d-grid gap-2 small">
                <div>
                  <span className="text-muted">Name:</span>{" "}
                  <span className="text-uppercase fw-semibold">
                    {enquiry.customer.fullName}
                  </span>
                </div>
                <div>
                  <span className="text-muted">Mobile:</span> {enquiry.customer.mobile}
                </div>
                <div>
                  <span className="text-muted">Email:</span>{" "}
                  {enquiry.customer.email || "-"}
                </div>
                <div>
                  <span className="text-muted">Address:</span>{" "}
                  <span className="text-uppercase">
                    {enquiry.customer.address}
                  </span>
                </div>
                <div>
                  <span className="text-muted">Aadhar:</span>{" "}
                  {enquiry.customer.aadharNo || "-"}
                </div>
                <div>
                  <span className="text-muted">PAN:</span> {enquiry.customer.panNo || "-"}
                </div>
                <div>
                  <span className="text-muted">Birth Date:</span>{" "}
                  {enquiry.customer.birthDate}
                </div>
              </div>
              <hr />
              <div className="d-grid gap-2 small">
                <div>
                  <span className="text-muted">Status:</span>{" "}
                  <span className="fw-semibold">{enquiry.status}</span>
                </div>
                <div>
                  <span className="text-muted">Assigned To:</span>{" "}
                  {enquiry.assignedToName || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">Admin Update</h6>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={form.status}
                    onChange={onChange}
                  >
                    <option value="NEW">NEW</option>
                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Assign Team Member</label>
                  <select
                    className="form-select"
                    name="assignedToUserId"
                    value={form.assignedToUserId}
                    onChange={onChange}
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Work-Type</label>
                  <select
                    className="form-select"
                    name="workTypeId"
                    value={form.workTypeId}
                    onChange={onChange}
                  >
                    <option value="">Select Work Type</option>
                    {workTypes.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Work Item</label>
                  <select
                    className="form-select"
                    name="workId"
                    value={form.workId}
                    onChange={onChange}
                    disabled={!form.workTypeId}
                  >
                    <option value="">Select Work Item (Optional)</option>
                    {filteredWorks.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} (Fee: ₹{Number(w.fee).toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Payment For Work</label>
                  <SpeechInput
                    as="input"
                    name="paymentAmount"
                    value={form.paymentAmount}
                    onChange={onChange}
                    inputMode="decimal"
                    placeholder="Amount"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Work</label>
                  <SpeechInput
                    as="textarea"
                    name="workDescription"
                    value={form.workDescription}
                    onChange={onChange}
                    rows={3}
                    placeholder="Work details"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Next Appointment Date</label>
                  <input
                    className="form-control"
                    name="nextAppointmentAt"
                    value={form.nextAppointmentAt}
                    onChange={onChange}
                    type="datetime-local"
                  />
                </div>

                <div className="col-12 d-flex justify-content-end">
                  <button
                    className="btn btn-primary"
                    disabled={!canSave}
                    onClick={onSave}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <h6 className="mb-3">Followups</h6>
              {followups.length === 0 ? (
                <div className="text-muted">No followups yet.</div>
              ) : (
                <div className="list-group">
                  {followups.map((f) => (
                    <div key={f.id} className="list-group-item">
                      <div className="d-flex justify-content-between">
                        <div className="fw-semibold">{f.createdByName}</div>
                        <div className="text-muted small">
                          {formatDateTime(f.createdAt)}
                        </div>
                      </div>
                      <div className="mt-1">{f.progressNote}</div>
                      <div className="text-muted small mt-1">
                        Next followup:{" "}
                        {f.nextFollowupAt
                          ? formatDateTime(f.nextFollowupAt)
                          : "-"}
                      </div>
                      {f.documents && f.documents.length ? (
                        <div className="mt-2">
                          <div className="small text-muted mb-1">Attachments:</div>
                          <ul className="list-group list-group-flush">
                            {f.documents.map((d) => (
                              <li
                                key={d.id}
                                className="list-group-item d-flex justify-content-between align-items-center py-1 px-0 border-0"
                              >
                                <div>
                                  <span className="small fw-semibold">
                                    {d.documentName}
                                  </span>
                                  <span className="small text-muted ms-2">
                                    {formatSize(d.fileSize)}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() =>
                                    downloadDocument(d.id, d.documentName)
                                  }
                                >
                                  Download
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

